import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

/**
 * User-side "Delete my account" confirmation dialog.
 * Requires the user to type DELETE (or their email) to confirm.
 */
export default function DeleteAccountDialog({ open, onOpenChange }) {
  const { deleteAccount } = useAuth();
  const { t } = useI18n();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!confirm.trim()) return;
    setBusy(true);
    const ok = await deleteAccount(confirm.trim());
    setBusy(false);
    if (ok) {
      toast.success(t("auth.delete_account_success"));
      onOpenChange(false);
      // Cookies are cleared by the API; AuthContext.user is already false
      window.location.href = "/login";
    } else {
      toast.error(t("auth.delete_account_no"));
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(v) => {
        if (busy) return;
        if (!v) setConfirm("");
        onOpenChange(v);
      }}
    >
      <AlertDialogContent data-testid="delete-account-dialog" className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-red-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            {t("auth.delete_account_title")}
          </AlertDialogTitle>
          <AlertDialogDescription>{t("auth.delete_account_body")}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="del-confirm" className="text-xs uppercase tracking-widest text-red-800">
            {t("auth.delete_account_confirm_label")}
          </Label>
          <Input
            id="del-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            data-testid="delete-account-confirm-input"
            className="font-mono"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="delete-account-no">
            {t("auth.delete_account_no")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onSubmit}
            disabled={busy || !confirm.trim()}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-account-yes"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.delete_account_yes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
